import React from 'react'
import { Link } from 'react-router-dom'
import ArrowMark from '@/components/ArrowMark'
import { PageHeaderBackdrop } from '@/components/PageHeader'
import SectionLabel from '@/components/SectionLabel'
import AnimatedNumber from '@/components/AnimatedNumber'
import ArchLineBg from '@/components/ArchLineBg'

function AboutHero() {
  return (
    <header style={{ paddingTop: 'var(--hdr-top)', paddingBottom: 'var(--hdr-bot)', position: 'relative', borderBottom: '1px solid var(--line-soft)', overflow: 'hidden' }}>
      <PageHeaderBackdrop variant="about" />
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--fg-3)', marginBottom: 24 }}>
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
            <span style={{ width: 22, height: 2, background: 'var(--accent)', borderRadius: 1 }} />
            <span style={{ width: 14, height: 2, background: 'var(--accent)', opacity: 0.5, borderRadius: 1 }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.04em' }}>About the practice</span>
        </div>
        <h1 className="display" style={{ fontSize: 'clamp(40px, 6vw, 80px)', color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.02, maxWidth: 1050 }}>
          A practice built on <span style={{ color: 'var(--accent)' }}>permanence</span>,<br />
          <span style={{ color: 'var(--fg-1)', fontWeight: 400 }}>and the people who choose it.</span>
        </h1>
        <p style={{ marginTop: 36, maxWidth: 720, fontSize: 19, color: 'var(--fg-1)', lineHeight: 1.6 }}>
          For 36 years SMT Construction has built a reputation on a quiet promise: every joint, every weld, every pour answers to a fifty-year horizon. Founded in 1989 and registered as a Private Limited firm in 1994, we&rsquo;re a Class C licensed contractor headquartered in Baneshwor Height, Kathmandu.
        </p>
      </div>
    </header>
  );
}

function AboutIntro() {
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-0)' }}>
      <div className="container">
        <SectionLabel title="The Practice" count="EST. 1989" />
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 96, marginTop: 64, alignItems: 'start' }} className="about-intro-grid">
          <div>
            <h2 className="display" style={{ fontSize: 'clamp(36px, 4.5vw, 60px)', color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
              From a single contractor in 1989 to one of Nepal&rsquo;s most trusted construction partners.
            </h2>
            <div style={{ marginTop: 36, fontSize: 17, color: 'var(--fg-1)', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 700 }}>
              <p>
                SMT Construction was established in 1989 with one aim &mdash; to raise the standard of construction services available in Nepal. We were registered as a Private Limited company in 1994 under the Private Limited Company Act of 2021 BS, and have since served as a Class C contractor for clients across the country.
              </p>
              <p>
                We&rsquo;ve helped build private homes, schools, hospitals, NGO facilities, government offices, and embassy compounds &mdash; each treated with the same engineering rigour and personal accountability. Many of our clients have stayed with us through multiple projects across multiple decades.
              </p>
              <p>
                Our work spans the four core construction trades &mdash; <strong style={{ color: 'var(--fg-0)' }}>civil, mechanical, electrical and sanitary</strong> &mdash; all under one roof. New buildings, renovations, seismic retrofits, security upgrades, and long-term maintenance are all delivered by the same in-house team.
              </p>
            </div>
          </div>
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { k: 'Founded', v: '1989', d: 'Baneshwor, Kathmandu' },
              { k: 'Registered', v: '1994', d: 'Private Limited · 2046-06-06 B.S.' },
              { k: 'License type', v: 'Class C', d: 'Department of Industry' },
              { k: 'Quality system', v: 'ISO 9001:2015', d: 'Certified across all trades' },
              { k: 'Headquarters', v: 'Baneshwor Height', d: 'Tanka Prasad Marg, Kathmandu' },
            ].map(row => (
              <div key={row.k} style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 14, padding: '18px 22px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{row.k}</div>
                <div style={{ fontFamily: 'var(--space)', fontSize: 18, fontWeight: 700, color: 'var(--fg-0)', marginTop: 4 }}>{row.v}</div>
                <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4 }}>{row.d}</div>
              </div>
            ))}
          </aside>
        </div>
      </div>
      <style>{`@media (max-width: 960px) { .about-intro-grid { grid-template-columns: 1fr !important; gap: 48px !important; } }`}</style>
    </section>
  );
}

function AboutNumbers() {
  const stats = [
    { v: 36, suf: '+', label: 'Years of building', sub: 'Continuously since 1989' },
    { v: 240, suf: '+', label: 'Projects delivered', sub: 'Across Nepal' },
    { v: 50, suf: '+', label: 'Active clients', sub: 'Many on multi-year frameworks' },
    { v: 150, suf: '+', label: 'Skilled crew', sub: 'In-house, all trades' },
  ];
  return (
    <section style={{ padding: '40px 0 120px', background: 'var(--bg-0)' }}>
      <div className="container">
        <div style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 24, padding: 'clamp(32px, 5vw, 64px)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32 }} className="about-stats">
          {stats.map((s, i) => (
            <div key={i} style={{
              borderRight: i < stats.length - 1 ? '1px solid var(--line-soft)' : 'none',
              paddingRight: i < stats.length - 1 ? 24 : 0,
            }}>
              <div className="display" style={{ fontSize: 'clamp(40px, 5vw, 64px)', color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>
                <AnimatedNumber value={s.v} suffix={s.suf} />
              </div>
              <div style={{ marginTop: 12, fontSize: 14, color: 'var(--fg-0)', fontWeight: 600 }}>{s.label}</div>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--fg-2)' }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .about-stats { grid-template-columns: 1fr 1fr !important; }
          .about-stats > * { border-right: none !important; padding-right: 0 !important; }
        }
        @media (max-width: 540px) { .about-stats { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function AboutValues() {
  const vals = [
    {
      t: 'Engineering-first',
      b: 'The structure decides the calendar &mdash; never the other way around. We refuse work where the engineering is dictated by the schedule, and that&rsquo;s why our buildings last.',
      icon: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6M9 12h.01M15 12h.01',
    },
    {
      t: 'Everything under one roof',
      b: 'Civil, mechanical, electrical and sanitary work all sit inside SMT. There&rsquo;s no third party to coordinate with, no second contract to chase &mdash; one team, one accountability.',
      icon: 'M2 12h20M2 12l4-4m-4 4l4 4M22 12l-4-4m4 4l-4 4',
    },
    {
      t: 'The same standard, every project',
      b: 'A family home gets the same engineering rigour as an embassy compound. We don&rsquo;t scale our care to the contract size &mdash; the spec, the safety practice and the quality control are identical.',
      icon: 'M9 11l3 3 8-8M20 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2h11',
    },
    {
      t: 'Long after handover',
      b: 'Most of our work comes from clients who&rsquo;ve been with us for decades. We carry a defect liability period on every project, and we mean it &mdash; site visits, warranty calls, and the same crew if you need them again.',
      icon: 'M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
  ];
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-1)', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <SectionLabel title="Operating Principles" count="04 commitments" />
        <h2 className="display" style={{ fontSize: 'clamp(36px, 5vw, 64px)', marginTop: 32, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.05, maxWidth: 880 }}>
          The way we work, on every single project.
        </h2>
        <div style={{ marginTop: 64, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }} className="values-grid">
          {vals.map((v, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 18, padding: 40, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d={v.icon} />
                </svg>
              </div>
              <h3 className="display" style={{ fontSize: 26, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.01em' }}>{v.t}</h3>
              <p style={{ fontSize: 15.5, color: 'var(--fg-1)', lineHeight: 1.65 }} dangerouslySetInnerHTML={{ __html: v.b }} />
            </div>
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 720px) { .values-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function AboutTimeline() {
  const events = [
    { year: '1989', title: 'Founded in Kathmandu', body: 'SMT Construction established at Baneshwor Height. First commission delivered within three trimesters.' },
    { year: '1994', title: 'Private Limited registration', body: 'Re-registered as SMT Construction (P.) Ltd. with Class C licence; mechanical and electrical divisions added in-house.' },
    { year: '2002', title: 'First international clients', body: 'Awarded the first multi-year embassy compound retrofit; framework agreements signed with development partners.' },
    { year: '2009', title: 'UN & multilateral work', body: 'Long-term framework agreements with UNDP, ILO, UNICEF and other multilateral lenders.' },
    { year: '2015', title: 'Post-earthquake rebuild', body: 'Mobilised crews for emergency hospital and school reconstruction across multiple districts following the Gorkha earthquake.' },
    { year: '2018', title: 'ISO 9001:2015 certification', body: 'Quality management system audited and certified across every trade division.' },
    { year: '2024', title: 'Modern delivery & BIM', body: 'BIM-coordinated delivery, digital project tracking, and live status reporting on every major commission.' },
  ];
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-0)', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <SectionLabel title="Our Journey · 1989 → 2026" count={`${events.length} milestones`} />
        <div style={{ marginTop: 64, position: 'relative' }}>
          <div style={{ position: 'absolute', left: 124, top: 24, bottom: 24, width: 2, background: 'linear-gradient(180deg, var(--accent) 0%, var(--line) 100%)' }} className="timeline-spine" />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {events.map((e, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '108px 32px 1fr', gap: 24, padding: '28px 0', alignItems: 'start' }} className="timeline-row">
                <div className="display" style={{ fontSize: 28, color: 'var(--accent)', fontWeight: 700, letterSpacing: '-0.01em' }}>{e.year}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', paddingTop: 6 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '3px solid var(--accent)', position: 'relative', zIndex: 2 }} />
                </div>
                <div style={{ paddingTop: 4 }}>
                  <h3 className="display" style={{ fontSize: 22, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.01em' }}>{e.title}</h3>
                  <p style={{ marginTop: 10, fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.6, maxWidth: 760 }}>{e.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 720px) {
          .timeline-spine { left: 78px !important; }
          .timeline-row { grid-template-columns: 70px 24px 1fr !important; gap: 12px !important; }
        }
      `}</style>
    </section>
  );
}

function AboutMethodology() {
  const phases = [
    { n: '01', t: 'Site planning & mobilisation', b: 'Site analysis, mobilisation plan signed off with client and consultant, site fencing, storage areas, temporary site office and restrooms.' },
    { n: '02', t: 'Earthworks', b: 'Foundation excavation and levelling, carried out exactly to drawings, specifications and instructions of the architect/engineer.' },
    { n: '03', t: 'Structural works', b: 'Foundations, concreting, masonry, window and door framing, with associated electrical and sanitary works integrated from day one.' },
    { n: '04', t: 'Finishing works', b: 'Plaster, flooring, painting, false ceilings, aluminium windows, electrical and sanitary fixtures, apron drains and plinth protection.' },
    { n: '05', t: 'Handover & defect liability', b: 'Formal handover to the client, followed by a 12-month defect liability period during which any genuine defects are rectified at our cost.' },
  ];
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-1)', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <SectionLabel title="How We Work" count="05 phases" />
        <h2 className="display" style={{ fontSize: 'clamp(36px, 5vw, 64px)', marginTop: 32, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.05, maxWidth: 860 }}>
          Every project follows the same proven path &mdash; brief to handover.
        </h2>
        <p style={{ marginTop: 16, fontSize: 16, color: 'var(--fg-2)', lineHeight: 1.6, maxWidth: 640 }}>
          The complexity changes, the discipline doesn&rsquo;t. We use the same methodology on a private home as we do on a hospital wing.
        </p>
        <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }} className="phases-grid">
          {phases.map((p, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 16, position: 'relative', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'var(--accent)', color: '#fff', fontFamily: 'var(--space)', fontSize: 13, fontWeight: 700 }}>{p.n}</span>
                <span style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
              </div>
              <h3 className="display" style={{ fontSize: 17, color: 'var(--fg-0)', fontWeight: 600, lineHeight: 1.3 }}>{p.t}</h3>
              <p style={{ fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55 }}>{p.b}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 32, padding: 24, background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 16, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, background: 'var(--accent-soft)', color: 'var(--accent-deep)', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 2a10 10 0 100 20 10 10 0 000-20z"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontFamily: 'var(--space)', fontWeight: 600, color: 'var(--fg-0)', fontSize: 15 }}>Health, safety & labour</div>
            <div style={{ fontSize: 14, color: 'var(--fg-2)', marginTop: 4, lineHeight: 1.5 }}>Standard PPE and first-aid kits on every site. We comply fully with Nepal labour law, including child-labour rules, and carry the required insurance on every project.</div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 1100px) { .phases-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 720px) { .phases-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function AboutCredentials() {
  const creds = [
    { k: 'License', v: 'Class C', d: 'Department of Industry · Nepal' },
    { k: 'Legal structure', v: 'Private Limited', d: 'Registered 2046-06-06 B.S. (1994)' },
    { k: 'Quality system', v: 'ISO 9001:2015', d: 'Certified · all four trades' },
  ];
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-0)', borderTop: '1px solid var(--line-soft)', position: 'relative', overflow: 'hidden' }}>
      <ArchLineBg variant="skyline" opacity={0.6} />
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <SectionLabel title="Credentials & Registrations" count={`${creds.length} credentials`} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 64, marginTop: 56, alignItems: 'start' }} className="creds-grid">
          <div>
            <h2 className="display" style={{ fontSize: 'clamp(32px, 4vw, 52px)', color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
              Properly registered. Properly audited. Properly insured.
            </h2>
            <p style={{ marginTop: 24, fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.6, maxWidth: 360 }}>
              Fully licensed, legally registered, and quality-certified — everything you need to verify before engaging us.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }} className="creds-cards">
            {creds.map((c, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 14, padding: '20px 24px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.k}</div>
                <div style={{ fontFamily: 'var(--space)', fontSize: 18, fontWeight: 700, color: 'var(--fg-0)', marginTop: 6 }}>{c.v}</div>
                <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4 }}>{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 980px) {
          .creds-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .creds-cards { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 540px) { .creds-cards { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>

  );
}

function AboutCTA() {
  return (
    <section style={{ padding: 'var(--sec-pad) 0', background: 'var(--bg-0)', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <div style={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F0EBE2 100%)',
          border: '1px solid var(--line-soft)', borderRadius: 24, padding: 'clamp(40px, 6vw, 80px)',
          display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 48, alignItems: 'center',
          boxShadow: '0 30px 60px -30px rgba(0,0,0,0.10)',
        }} className="about-cta">
          <div>
            <SectionLabel title="What's Next" />
            <h2 className="display" style={{ fontSize: 'clamp(32px, 4.5vw, 56px)', color: 'var(--fg-0)', marginTop: 24, lineHeight: 1.05, fontWeight: 600, letterSpacing: '-0.02em' }}>
              See the work, or get in touch directly.
            </h2>
            <p style={{ marginTop: 20, fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.6, maxWidth: 480 }}>
              Browse our recent commissions and ongoing projects, or send us a brief and we&rsquo;ll respond within one working day.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Link to="/projects" className="btn btn-primary" style={{ justifyContent: 'space-between' }}>
              <span>See our projects</span><span className="arrow-circle"><ArrowMark /></span>
            </Link>
            <Link to="/contact" className="btn btn-ghost" style={{ justifyContent: 'space-between' }}>
              <span>Send a brief</span><span className="arrow-circle"><ArrowMark /></span>
            </Link>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .about-cta { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="page">
      <AboutHero />
      <AboutIntro />
      <AboutNumbers />
      <AboutValues />
      <AboutTimeline />
      <AboutMethodology />
      <AboutCredentials />
      <AboutCTA />
    </div>
  );
}
